import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  CreditCard, XCircle, AlertCircle,
  ArrowLeft, Loader2, RefreshCw
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { formatDate } from '@/lib/utils'

interface Subscription {
  id: string
  status: string
  current_period_start: string
  current_period_end: string
  cancel_at_period_end: boolean
  amount: number
  currency: string
  tier?: string
}

export default function Subscription() {
  const { user, refreshUser } = useAuth()
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [canceling, setCanceling] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSubscriptions()
  }, [])

  const loadSubscriptions = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiClient.getUserSubscriptions()
      // Handle different response formats
      if (Array.isArray(data)) {
        setSubscriptions(data)
      } else if (data.subscriptions) {
        setSubscriptions(data.subscriptions)
      } else {
        setSubscriptions([])
      }
    } catch (err: any) {
      console.error('Failed to load subscriptions:', err)
      setError(err.message || 'Failed to load subscriptions')
      setSubscriptions([])
    } finally {
      setLoading(false)
    }
  }

  const handleCancelSubscription = async (subscriptionId: string) => {
    if (!confirm('Are you sure you want to cancel this subscription? You will lose access to premium features at the end of the billing period.')) {
      return
    }

    try {
      setCanceling(subscriptionId)
      setError(null)
      await apiClient.cancelSubscription(subscriptionId)
      
      // Refresh subscriptions and user data
      await loadSubscriptions()
      if (refreshUser) {
        await refreshUser()
      }
      
      alert('Subscription canceled successfully. You will retain access until the end of the current billing period.')
    } catch (err: any) {
      console.error('Failed to cancel subscription:', err)
      setError(err.message || 'Failed to cancel subscription')
    } finally {
      setCanceling(null)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Active</Badge>
      case 'canceled':
      case 'canceling':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">Canceling</Badge>
      case 'past_due':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/50">Past Due</Badge>
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/50">{status}</Badge>
    }
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100) // Convert cents to dollars
  }

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/app/dashboard" className="inline-flex items-center text-gray-400 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent font-mono">
              Subscription Management
            </h1>
            <p className="text-gray-400 mt-2 font-mono text-sm">
              Manage your subscription and billing information
            </p>
          </div>
          <Button
            variant="outline"
            onClick={loadSubscriptions}
            disabled={loading}
            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Current Subscription Tier */}
        <Card className="bg-gray-900/50 border-cyan-500/30">
          <CardHeader>
            <CardTitle className="text-cyan-400 font-mono">Current Plan</CardTitle>
            <CardDescription className="text-gray-400">
              Your active subscription tier and usage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-white capitalize font-mono mb-2">
                  {user?.subscription_tier || 'Starter'}
                </div>
                <div className="text-sm text-gray-400 font-mono">
                  {user?.request_limit === -1 
                    ? 'Unlimited queries per month'
                    : `${user?.request_limit || 0} queries per month`
                  }
                </div>
                <div className="text-sm text-gray-500 mt-1 font-mono">
                  Used: {user?.requests_used || 0} / {user?.request_limit === -1 ? '∞' : user?.request_limit || 0}
                </div>
              </div>
              <div className="text-right">
                {user?.subscription_tier === 'freemium' ? (
                  <Link to="/pricing">
                    <Button className="bg-green-500 hover:bg-green-600 text-black">
                      Upgrade Plan
                    </Button>
                  </Link>
                ) : (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                    Active
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Card className="bg-red-500/10 border-red-500/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <p className="text-red-400">{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscriptions List */}
        <Card className="bg-gray-900/50 border-cyan-500/30">
          <CardHeader>
            <CardTitle className="text-cyan-400 font-mono">Active Subscriptions</CardTitle>
            <CardDescription className="text-gray-400">
              Manage your subscription plans and billing cycles
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
              </div>
            ) : subscriptions.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 font-mono mb-4">No active subscriptions found</p>
                {user?.subscription_tier === 'freemium' && (
                  <Link to="/pricing">
                    <Button className="bg-green-500 hover:bg-green-600 text-black">
                      View Plans
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {subscriptions.map((subscription) => (
                  <div
                    key={subscription.id}
                    className="border border-gray-700 rounded-lg p-4 bg-black/50"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-cyan-400" />
                        <div>
                          <div className="font-semibold text-white capitalize font-mono">
                            {subscription.tier || 'Subscription'} Plan
                          </div>
                          <div className="text-sm text-gray-400 font-mono">
                            ID: {subscription.id.substring(0, 20)}...
                          </div>
                        </div>
                      </div>
                      {getStatusBadge(subscription.status)}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-gray-500 mb-1 font-mono">Amount</div>
                        <div className="text-lg font-bold text-white font-mono">
                          {formatCurrency(subscription.amount, subscription.currency)}
                          <span className="text-sm text-gray-400 ml-1">/month</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1 font-mono">Next Billing</div>
                        <div className="text-sm text-gray-300 font-mono">
                          {subscription.current_period_end 
                            ? formatDate(subscription.current_period_end)
                            : 'N/A'
                          }
                        </div>
                      </div>
                    </div>

                    {subscription.status.toLowerCase() === 'active' && !subscription.cancel_at_period_end && (
                      <Button
                        variant="outline"
                        onClick={() => handleCancelSubscription(subscription.id)}
                        disabled={canceling === subscription.id}
                        className="w-full border-red-500/50 text-red-400 hover:bg-red-500/20"
                      >
                        {canceling === subscription.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Canceling...
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-2" />
                            Cancel Subscription
                          </>
                        )}
                      </Button>
                    )}

                    {subscription.cancel_at_period_end && (
                      <div className="flex items-center gap-2 text-yellow-400 text-sm font-mono">
                        <AlertCircle className="w-4 h-4" />
                        Subscription will cancel at the end of the billing period
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upgrade CTA */}
        {user?.subscription_tier === 'freemium' && (
          <Card className="bg-gradient-to-r from-green-500/10 to-cyan-500/10 border-green-500/50">
            <CardContent className="pt-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2 font-mono">
                  Upgrade to unlock more features
                </h3>
                <p className="text-gray-400 mb-4 font-mono text-sm">
                  Get access to more queries, advanced features, and priority support
                </p>
                <Link to="/pricing">
                  <Button className="bg-green-500 hover:bg-green-600 text-black">
                    View Plans
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

